/* ============================================================
   sl-loop.js — loop-by studiolab
   VERSION: 1.2.2

   Four jobs:

   1) SLOT MEASURING. The slot width is written to --sl-slot in
      px rather than left as a CSS percentage, because a
      percentage width is unreliable inside a `width: max-content`
      marquee track (the parent is sized by its children, so the
      percentage has nothing stable to resolve against). The
      per-view count and gap still live in CSS, so all the
      breakpoint logic stays in the stylesheet — this only reads
      them back and does the arithmetic.

   2) CLONE-TO-FILL. The original slides are repeated until the
      track is wider than the viewport plus one full set, then
      the animation shifts by exactly one set. Repeating once is
      not enough: 3 slides at 5-per-view would leave a hole.
      Shifting by one set (rather than -50% of the track) is also
      what keeps the seam invisible — a -50% loop lands half a
      gap off whenever there is a gap between slides.

   3) MANUAL MODE. Mouse drag, prev/next arrows, pagination dots,
      and a rAF-throttled scroll observer that keeps the controls
      in sync. Touch swipe is native overflow scrolling.

   4) DRAG ON THE AUTOMATIC LOOP. The drift is a CSS animation,
      which the compositor runs for free — but a compositor
      animation cannot be grabbed, so there is nothing to interact
      with. When drag is on, the same drift is therefore re-driven
      from rAF: a swipe or a mouse drag takes it over mid-flight,
      throws it, and hands it back as the throw decays. Geometry
      is unchanged (same cloned track, same one-set shift) — only
      what writes the transform differs. Touch keeps its own
      listeners (axis-lock so a vertical swipe still scrolls the
      page). Mouse/pen use Pointer Events with capture, so the
      strip follows even if the cursor leaves the viewport.

   Idempotent: safe to re-run on shopify:section:load.
   ============================================================ */
(function () {
  'use strict';

  if (window.SLLoop) {
    return;
  }

  var EDITOR_ATTRS = [
    'data-shopify-editor-block',
    'data-shopify-editor-block-id',
    'data-shopify-block-id'
  ];

  function stripEditorAttrs(node) {
    EDITOR_ATTRS.forEach(function (attr) {
      if (node.hasAttribute && node.hasAttribute(attr)) node.removeAttribute(attr);
    });
    var nested = node.querySelectorAll ? node.querySelectorAll('*') : [];
    for (var i = 0; i < nested.length; i++) {
      EDITOR_ATTRS.forEach(function (attr) {
        if (nested[i].hasAttribute(attr)) nested[i].removeAttribute(attr);
      });
    }
  }

  function getTrack(viewport) {
    return viewport.querySelector('[data-sl-track]');
  }

  function getOriginals(track) {
    var out = [];
    for (var i = 0; i < track.children.length; i++) {
      if (!track.children[i].hasAttribute('data-sl-clone')) out.push(track.children[i]);
    }
    return out;
  }

  function removeClones(track) {
    var clones = track.querySelectorAll('[data-sl-clone]');
    for (var i = 0; i < clones.length; i++) {
      clones[i].parentNode.removeChild(clones[i]);
    }
  }

  function getGap(track) {
    var cs = getComputedStyle(track);
    return parseFloat(cs.columnGap) || parseFloat(cs.gap) || 0;
  }

  /* Reads --sl-count / --sl-gap (set per breakpoint in the section's
     CSS) and writes the resulting px slot width back to the section
     root, where the slides inherit it. */
  function measureSlot(sectionRoot, viewport) {
    var cs = getComputedStyle(sectionRoot);
    var count = parseFloat(cs.getPropertyValue('--sl-count')) || 1;
    var gap = parseFloat(cs.getPropertyValue('--sl-gap')) || 0;
    var width = viewport.clientWidth;
    if (!width || count <= 0) return;

    var slot = (width - (count - 1) * gap) / count;
    if (slot > 0) sectionRoot.style.setProperty('--sl-slot', slot + 'px');
  }

  function setupAutoLoop(sectionRoot, viewport) {
    var track = getTrack(viewport);
    if (!track) return;

    removeClones(track);

    var originals = getOriginals(track);
    if (originals.length === 0) return;

    var gap = getGap(track);

    /* One "set" = every original slide plus the gap that follows each
       of them, so consecutive sets tile at a constant rhythm. */
    var setWidth = 0;
    for (var i = 0; i < originals.length; i++) {
      setWidth += originals[i].getBoundingClientRect().width + gap;
    }
    if (setWidth <= 0) return;

    /* Enough sets to cover the viewport AND leave one spare set to
       shift into. Minimum 2 so there is always something following. */
    var needed = Math.max(2, Math.ceil(viewport.clientWidth / setWidth) + 1);

    for (var rep = 1; rep < needed; rep++) {
      for (var j = 0; j < originals.length; j++) {
        var clone = originals[j].cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-sl-clone', '');
        stripEditorAttrs(clone);

        /* Clones stay clickable — a visitor clicking a visible clone
           should navigate — but must not add duplicate tab stops. */
        var links = clone.querySelectorAll('a[href]');
        for (var k = 0; k < links.length; k++) links[k].setAttribute('tabindex', '-1');

        track.appendChild(clone);
      }
    }

    var speed = parseFloat(getComputedStyle(sectionRoot).getPropertyValue('--sl-speed')) || 60;
    track.style.setProperty('--sl-shift', setWidth + 'px');
    track.style.setProperty('--sl-duration', (setWidth / speed) + 's');

    /* The rAF drift wraps on this same distance, and it is remeasured
       on every resize, so it is published rather than recomputed. */
    viewport.slSetWidth = setWidth;

    viewport.classList.add('is-ready');
  }

  /* ── DRAG ON THE AUTOMATIC LOOP (touch + mouse) ────────────── */

  var FRICTION  = 0.94;  /* share of a throw kept per 60fps frame      */
  var MAX_FLING = 4200;  /* px/s ceiling, so a violent swipe stays sane */
  var AXIS_LOCK = 5;     /* px of travel before the gesture picks an axis */
  var TAP_SLOP  = 6;     /* px that still counts as a tap, not a drag   */
  var VEL_STALE = 100;   /* ms — a finger resting this long lifts dead  */

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* Drag is a setting, not a pointer-type. The compositor
     animation cannot be grabbed on any device, so when the
     merchant enables drag the drift always moves to rAF. */
  function wantsJsDrive(viewport) {
    return viewport.dataset.mode === 'auto' && viewport.dataset.slDrag === 'true';
  }

  function currentTranslateX(el) {
    var value = getComputedStyle(el).transform;
    if (!value || value === 'none') return 0;
    var inner = value.match(/\((.+)\)/);
    if (!inner) return 0;
    var parts = inner[1].split(',');
    /* translateX sits at index 4 of matrix(), index 12 of matrix3d(). */
    return parseFloat(parts.length > 6 ? parts[12] : parts[4]) || 0;
  }

  function setupJsDrive(sectionRoot, viewport) {
    if (viewport.dataset.slJsDrive === 'true') return;
    var track = getTrack(viewport);
    if (!track) return;
    viewport.dataset.slJsDrive = 'true';
    viewport.classList.add('is-js-driven');

    /* Reading the live matrix means a takeover that happens once the
       CSS animation is already running (a resize down into mobile
       width) continues from where the strip visually is, not from 0. */
    var offset = -currentTranslateX(track);
    var velocity = 0;
    var dragging = false;
    var hovering = false;
    var rafId = null;
    var lastFrame = 0;
    var io = null;

    var pauseOnHover = viewport.dataset.slHoverPause === 'true';
    var dirSign = viewport.dataset.direction === 'right' ? -1 : 1;

    function driftSpeed() {
      return parseFloat(getComputedStyle(sectionRoot).getPropertyValue('--sl-speed')) || 60;
    }
    function isPaused() {
      return (pauseOnHover && hovering) || viewport.classList.contains('is-paused');
    }

    function frame(now) {
      /* shopify:section:load swaps the section's DOM wholesale; without
         this the old loop would animate a detached node forever. */
      if (!viewport.isConnected) return teardown();

      rafId = requestAnimationFrame(frame);

      var dt = (now - lastFrame) / 1000;
      lastFrame = now;
      /* First frame, or a return from a backgrounded tab, would
         otherwise integrate one enormous step. */
      if (!(dt > 0) || dt > 0.1) dt = 0.016;

      var width = viewport.slSetWidth || 0;
      if (width <= 0) return;

      if (!dragging) {
        var drift = isPaused() || prefersReducedMotion() ? 0 : driftSpeed() * dirSign;
        /* A throw is an impulse on top of the drift, not a replacement
           for it, so the strip eases back to its own rhythm instead of
           stopping dead and restarting. */
        offset += (drift + velocity) * dt;
        velocity *= Math.pow(FRICTION, dt * 60);
        if (Math.abs(velocity) < 2) velocity = 0;
      }

      /* One set is one full repetition, so folding the offset back
         into [0, setWidth) is visually a no-op and keeps the number
         from growing without bound. */
      offset = ((offset % width) + width) % width;
      track.style.transform = 'translateX(' + -offset + 'px)';
    }

    function start() {
      if (rafId !== null) return;
      lastFrame = window.performance && performance.now ? performance.now() : Date.now();
      rafId = requestAnimationFrame(frame);
    }
    function stop() {
      if (rafId === null) return;
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    function teardown() {
      stop();
      if (io) io.disconnect();
      if (viewport.slLoopDriveTeardown === teardown) viewport.slLoopDriveTeardown = null;
    }

    /* Theme Editor unloads replace the section DOM. Keep the active
       driver reachable so the shared lifecycle can stop it immediately,
       instead of waiting for the next animation frame to notice. */
    viewport.slLoopDriveTeardown = teardown;

    var startX = 0, startY = 0, startOffset = 0;
    var lastX = 0, lastT = 0, sampled = 0;
    var axis = null, moved = false;
    var suppressClick = false;

    viewport.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      var touch = e.touches[0];
      startX = lastX = touch.clientX;
      startY = touch.clientY;
      startOffset = offset;
      lastT = e.timeStamp;
      axis = null;
      moved = false;
      sampled = 0;
      velocity = 0; /* a finger down catches the strip, killing the last throw */
      suppressClick = false;
      start();
    }, { passive: true });

    viewport.addEventListener('touchmove', function (e) {
      if (e.touches.length !== 1) return;
      var touch = e.touches[0];
      var dx = touch.clientX - startX;
      var dy = touch.clientY - startY;

      /* The axis is decided once per gesture and never revisited: a
         swipe that started vertical stays a page scroll, so the
         carousel can never trap the page. */
      if (axis === null) {
        if (Math.abs(dx) < AXIS_LOCK && Math.abs(dy) < AXIS_LOCK) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (axis === 'x') {
          dragging = true;
          viewport.classList.add('is-dragging');
        }
      }
      if (axis !== 'x') return;

      e.preventDefault();
      if (Math.abs(dx) > TAP_SLOP) moved = true;
      offset = startOffset - dx;

      var dt = (e.timeStamp - lastT) / 1000;
      /* Guards against dividing by a near-zero interval. Kept below the
         ~8.3ms of a 120Hz screen so high-refresh phones still sample. */
      if (dt > 0.004) {
        /* Sampled between consecutive moves rather than across the whole
           gesture, so the throw follows the last flick and not the
           average of a long slow drag. */
        var instant = -(touch.clientX - lastX) / dt;
        sampled = sampled * 0.7 + instant * 0.3;
        lastX = touch.clientX;
        lastT = e.timeStamp;
      }
    }, { passive: false });

    function release(e) {
      if (axis === 'x') {
        /* A finger that came to rest before lifting is placing the
           strip, not throwing it. */
        var stale = e && (e.timeStamp - lastT) > VEL_STALE;
        velocity = stale ? 0 : Math.max(-MAX_FLING, Math.min(MAX_FLING, sampled));
      }
      /* Click fires after pointerup/touchend. A real drag must not
         follow the slide link; a tap/click must. Capture that here
         so the flag survives until the click listener runs. */
      suppressClick = moved;
      dragging = false;
      axis = null;
      moved = false;
      sampled = 0;
      viewport.classList.remove('is-dragging');
    }

    viewport.addEventListener('touchend', release);
    viewport.addEventListener('touchcancel', release);

    /* Mouse / pen. Touch keeps the listeners above so a vertical
       finger still scrolls the page (axis lock + touch-action).
       Pointer capture is delayed until the pointer has actually
       dragged: capturing on pointerdown retargets pointerup and
       click onto the viewport, so the slide <a> never navigates.
       After TAP_SLOP we capture so the strip still follows if the
       cursor leaves the viewport. */
    var pointerId = null;

    viewport.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      if (e.button !== 0) return;
      if (pointerId !== null) return;
      pointerId = e.pointerId;
      startX = lastX = e.clientX;
      startY = e.clientY;
      startOffset = offset;
      lastT = e.timeStamp;
      axis = null;
      moved = false;
      sampled = 0;
      velocity = 0; /* mousedown catches the strip, same as a finger */
      dragging = true; /* freeze under the cursor so pointerup stays on the same card */
      suppressClick = false;
      start();
    });

    viewport.addEventListener('pointermove', function (e) {
      if (e.pointerType === 'touch') return;
      if (e.pointerId !== pointerId) return;
      var dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > TAP_SLOP) {
        moved = true;
        axis = 'x';
        dragging = true;
        viewport.classList.add('is-dragging');
        try { viewport.setPointerCapture(e.pointerId); } catch (err) {}
      }
      if (!moved) return;
      e.preventDefault();
      offset = startOffset - dx;

      var dt = (e.timeStamp - lastT) / 1000;
      if (dt > 0.004) {
        var instant = -(e.clientX - lastX) / dt;
        sampled = sampled * 0.7 + instant * 0.3;
        lastX = e.clientX;
        lastT = e.timeStamp;
      }
    }, { passive: false });

    function releasePointer(e) {
      if (e.pointerType === 'touch') return;
      if (pointerId === null || e.pointerId !== pointerId) return;
      pointerId = null;
      release(e);
    }

    viewport.addEventListener('pointerup', releasePointer);
    viewport.addEventListener('pointercancel', releasePointer);
    viewport.addEventListener('lostpointercapture', function (e) {
      if (pointerId === null || e.pointerId !== pointerId) return;
      pointerId = null;
      release(e);
    });

    viewport.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    /* A swipe/drag that ends on top of a slide must not follow its link.
       A tap/click with no drag must. */
    viewport.addEventListener('click', function (e) {
      if (!suppressClick) return;
      e.preventDefault();
      e.stopPropagation();
      suppressClick = false;
    }, true);

    if (pauseOnHover) {
      viewport.addEventListener('mouseenter', function () { hovering = true; });
      viewport.addEventListener('mouseleave', function () { hovering = false; });
    }

    /* Off-screen the strip has no reason to burn frames — this is the
       one thing the CSS animation got for free that rAF does not. */
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(function (entries) {
        if (entries[entries.length - 1].isIntersecting) start();
        else stop();
      }, { rootMargin: '200px 0px' });
      io.observe(viewport);
    } else {
      start();
    }
  }

  function setupManualDrag(viewport) {
    if (viewport.dataset.slDragInit === 'true') return;
    viewport.dataset.slDragInit = 'true';

    var isDown = false;
    var startX = 0;
    var startScroll = 0;
    var moved = false;

    viewport.addEventListener('mousedown', function (e) {
      isDown = true;
      moved = false;
      viewport.classList.add('is-dragging');
      startX = e.pageX - viewport.offsetLeft;
      startScroll = viewport.scrollLeft;
    });
    viewport.addEventListener('mouseleave', function () {
      isDown = false;
      viewport.classList.remove('is-dragging');
    });
    viewport.addEventListener('mouseup', function () {
      isDown = false;
      viewport.classList.remove('is-dragging');
    });
    viewport.addEventListener('mousemove', function (e) {
      if (!isDown) return;
      e.preventDefault();
      var x = e.pageX - viewport.offsetLeft;
      var walk = (x - startX) * 1.5;
      if (Math.abs(walk) > 4) moved = true;
      viewport.scrollLeft = startScroll - walk;
    });

    /* A drag that ends on top of a slide must not follow its link. */
    viewport.addEventListener('click', function (e) {
      if (!moved) return;
      e.preventDefault();
      e.stopPropagation();
      moved = false;
    }, true);
  }

  function getStep(viewport) {
    var track = getTrack(viewport);
    if (!track) return 0;
    var first = track.firstElementChild;
    if (!first) return 0;
    return first.getBoundingClientRect().width + getGap(track);
  }

  function setupManualControls(sectionRoot, viewport) {
    if (viewport.dataset.slCtrlInit === 'true') return;
    viewport.dataset.slCtrlInit = 'true';

    var carousel = viewport.parentElement;
    if (!carousel) return;

    var prevBtn = carousel.querySelector('[data-sl-prev]');
    var nextBtn = carousel.querySelector('[data-sl-next]');
    var dots = sectionRoot.querySelectorAll('[data-sl-dot-index]');

    function updateState() {
      var max = viewport.scrollWidth - viewport.clientWidth;
      var here = viewport.scrollLeft;
      if (prevBtn) prevBtn.disabled = here <= 0;
      if (nextBtn) nextBtn.disabled = here >= max - 1;

      if (dots.length) {
        var step = getStep(viewport);
        var idx = step > 0 ? Math.round(here / step) : 0;
        if (idx < 0) idx = 0;
        if (idx > dots.length - 1) idx = dots.length - 1;
        for (var i = 0; i < dots.length; i++) {
          if (i === idx) {
            dots[i].classList.add('is-active');
          } else {
            dots[i].classList.remove('is-active');
          }
        }
      }
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        var step = getStep(viewport);
        if (step > 0) viewport.scrollBy({ left: -step, behavior: 'smooth' });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        var step = getStep(viewport);
        if (step > 0) viewport.scrollBy({ left: step, behavior: 'smooth' });
      });
    }
    for (var i = 0; i < dots.length; i++) {
      (function (idx, dot) {
        dot.addEventListener('click', function () {
          var step = getStep(viewport);
          if (step > 0) viewport.scrollTo({ left: idx * step, behavior: 'smooth' });
        });
      })(i, dots[i]);
    }

    var rafId = null;
    function schedule() {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(function () {
        rafId = null;
        updateState();
      });
    }

    viewport.addEventListener('scroll', schedule);

    /* Exposed so a resize (or a re-init after the section was laid out
       at a different size) can re-sync the controls. Without this the
       arrows can latch disabled: updateState runs once at init, and if
       the viewport measured 0-width at that moment `next` is disabled,
       which prevents the click that would produce the scroll event that
       would re-enable it. */
    viewport.slUpdateControls = updateState;
    updateState();
  }

  function destroyViewport(viewport) {
    if (!viewport) return;
    if (viewport.slResizeFrame !== null && viewport.slResizeFrame !== undefined) {
      cancelAnimationFrame(viewport.slResizeFrame);
      viewport.slResizeFrame = null;
    }
    if (viewport.slResizeObserver) {
      viewport.slResizeObserver.disconnect();
      viewport.slResizeObserver = null;
    }
    if (viewport.slLoopDriveTeardown) viewport.slLoopDriveTeardown();
  }

  function initViewport(viewport) {
    var sectionRoot = viewport.closest('[id^="sl-section-"]');
    if (!sectionRoot) return;

    measureSlot(sectionRoot, viewport);

    if (viewport.dataset.mode === 'auto' || viewport.dataset.mode === 'step') {
      setupAutoLoop(sectionRoot, viewport);
      if (viewport.dataset.mode === 'auto' && wantsJsDrive(viewport)) setupJsDrive(sectionRoot, viewport);
    } else {
      setupManualDrag(viewport);
      setupManualControls(sectionRoot, viewport);
      /* setupManualControls is bind-once; this re-syncs the arrow and
         dot state every time init runs against fresh measurements. */
      if (viewport.slUpdateControls) viewport.slUpdateControls();
    }

    /* Slot width, set width and loop distance all depend on the
       viewport width, so everything is re-derived when it changes. */
    if (viewport.dataset.slResizeInit !== 'true' && typeof ResizeObserver !== 'undefined') {
      viewport.dataset.slResizeInit = 'true';
      viewport.slObservedWidth = viewport.clientWidth;
      viewport.slResizeFrame = null;
      var ro = new ResizeObserver(function () {
        if (!viewport.isConnected) return destroyViewport(viewport);

        /* Geometry only depends on width. The Theme Editor changes
           section height frequently while controls are edited; cloning
           the entire track for those notifications was wasted work. */
        var width = viewport.clientWidth;
        if (Math.abs(width - viewport.slObservedWidth) < 0.5) return;
        viewport.slObservedWidth = width;

        if (viewport.slResizeFrame !== null) return;
        viewport.slResizeFrame = requestAnimationFrame(function () {
          viewport.slResizeFrame = null;
          if (!viewport.isConnected) return destroyViewport(viewport);
          measureSlot(sectionRoot, viewport);
          if (viewport.dataset.mode === 'auto' || viewport.dataset.mode === 'step') {
            setupAutoLoop(sectionRoot, viewport);
            /* Bind-once: a live rAF drift is never handed back to a
               CSS animation mid-flight (that seam is not worth it). */
            if (viewport.dataset.mode === 'auto' && wantsJsDrive(viewport)) setupJsDrive(sectionRoot, viewport);
          } else if (viewport.slUpdateControls) {
            viewport.slUpdateControls();
          }
        });
      });
      viewport.slResizeObserver = ro;
      ro.observe(viewport);
    }
  }

  function initAll(scope) {
    var root = scope || document;
    if (root.matches && root.matches('[data-sl-carousel]')) initViewport(root);
    var nodes = root.querySelectorAll('[data-sl-carousel]');
    for (var i = 0; i < nodes.length; i++) initViewport(nodes[i]);
  }

  function destroyAll(scope) {
    var root = scope || document;
    if (root.matches && root.matches('[data-sl-carousel]')) destroyViewport(root);
    var nodes = root.querySelectorAll ? root.querySelectorAll('[data-sl-carousel]') : [];
    for (var i = 0; i < nodes.length; i++) destroyViewport(nodes[i]);
  }

  window.SLLoop = { init: initAll, destroy: destroyAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* Theme Editor lifecycle */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    destroyAll(e.target);
  });
  document.addEventListener('shopify:block:select', function (e) {
    var viewport = e.target.closest && e.target.closest('[data-sl-carousel][data-mode="auto"]');
    if (viewport) viewport.classList.add('is-paused');
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var viewport = e.target.closest && e.target.closest('[data-sl-carousel][data-mode="auto"]');
    if (viewport) viewport.classList.remove('is-paused');
  });
})();
